import { action, computed } from 'mobx';
import objectPath from 'object-path';
import Alert from 'react-s-alert';

import { AppManager, Variables, Input } from 'core/index';

import { headerStore } from 'apps/components/header/app';
import { clientStore } from 'apps/client/app';
import { PaymentSpaceApp } from 'apps/payment/app';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';

import { InviteApp } from 'apps/group/app';
import { InitiativeModel, InitiativeApp } from 'apps/initiative/app';
import { InitiativeQuery, InitiativeMutation } from 'apps/initiative/gql/index';

export class InitiativeAppManager extends AppManager {
  static registryName = 'InitiativeAppManager';
  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('initiative', InitiativeApp);

    this.variables = new Variables({
      slug: () => this.routerStore.params.get('initiative'),
    });

    this.initiativeInput = new Input({
      id: () => this.initiative.id,
      slug: () => this.initiative.slug,
      avatar: () => {
        if (this.initiative.avatar) {
          return this.initiative.avatar.id;
        }
        return null;
      },
      title: () => this.initiative.title,
      description: () => this.initiative.description,
      tags: () =>
        this.initiative.tags.map(tag => ({
          id: tag.id,
          name: tag.name,
          deleted: tag.deleted,
        })),
      socialAccounts: () =>
        this.initiative.socialAccounts.map(social => ({
          id: social.id,
          accountType: social.accountType,
          accountName: social.accountName,
          deleted: social.deleted,
        })),
    });

    this.variables.onChange(['slug'], this.newSlug);
  }

  @computed
  get loading() {
    return this.queries.get('initiative')?.loading;
  }

  @action
  newSlug = () => {
    this.NewInitiativeQuery();
  };

  @computed
  get pendingInvitation() {
    const { pendingInvitations } = clientStore;
    const inSpaceID = objectPath.get(this, 'initiative.inSpace.id');
    if (!inSpaceID || !pendingInvitations) return null;
    const invite = pendingInvitations.find(
      ({ group }) => objectPath.get(group, 'space.id') === inSpaceID,
    );
    return invite ? new InviteApp(null, { id: invite.id }) : null;
  }

  @action
  NewInitiativeQuery = () => {
    const q = new InitiativeQuery(this, {
      queryKey: 'initiative',
      onSysErr: this.onInitiativeError,
    });
    q.process(invites => invites, clientStore.SetPendingInvitations, {
      subPath: 'loggedInUser.pendingInvites',
    });
    q.process(
      initiative => InitiativeModel.Load(initiative),
      this.InitiativeUpdate,
      { subPath: 'initiative.result' },
    );
    q.query();
  };

  @action
  onInitiativeError = () => {
    Alert.error('Hmm...We failed to load this initiative. Please try again.');
  };

  @action
  InitiativeUpdate = initiativeModel => {
    this.initiative = initiativeModel;
    if (this.initiative) {
      headerStore.SetPage(initiativeModel);
    }
  };

  @action
  SubmitInitiative = options => {
    this.mutations.push(InitiativeMutation.New(this.initiativeInput, options));
  };

  // FIXME This code is repeated at least in apps/profile/main/appManager
  @action Subscribe = params => {
    const subscriptionAppManager = new SubscriptionAppManager(this, params);
    const paymentSpace = new PaymentSpaceApp(subscriptionAppManager, params);
    if (!paymentSpace || (paymentSpace && !paymentSpace.preview)) {
      subscriptionAppManager.NewSubscriptionQuery({
        preview: true,
      });
    }
    paymentSpace.ConfirmSubscribe();
  };
}
